import { score100 } from "@/lib/scoreDisplay";
import { SustainabilityOverview } from "@/components/report/SustainabilityOverview";
import AssessmentMethodAudit from "@/components/AssessmentMethodAudit";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, BarChart3, Cloud, Download, Droplets, Factory, Leaf,
  Loader2, Recycle, ShieldCheck, Sparkles, Target, Box, Weight, CalendarDays,
} from "lucide-react";
import { API_BASE_URL } from "../config/api";
import {
  ESGE_COLORS, PARAM_KEYS, PARAM_UNITS, ADVANCED_PL as PL, advancedUnit, evidenceSummary, stageScoreNote, STAGES_ORDER, STAGE_FULL,
  aggregateStageInputs, buildCountryInferredRows, buildParamsByStage,
  classifyStage, criticalityLadder, heatColor, productBuckets, top5Actions,
} from "@/lib/reportUtils";
import { ParameterDonuts } from "@/components/report/ParameterDonuts";
import { ParameterDominance } from "@/components/report/ParameterDominance";
import { PollutionDriver } from "@/components/report/PollutionDriver";
import { RootCauseAnalysis } from "@/components/report/RootCauseAnalysis";
import { VariabilityIndicator } from "@/components/report/VariabilityIndicator";
import { InputAudit } from "@/components/report/InputAudit";
import { exportAdvancedReportToPdf } from "@/lib/advancedPdfExport";
import { BRAND, TOSS_LOGO_URL as LOGO_URL } from "@/lib/branding";
import "./AdvancedAssessmentReport.css";
import "./AdvancedAssessmentReport.print.css";

const API = API_BASE_URL;

const getRatingBand = (score) => score >= 8
  ? { label: "Excellent", color: "#1B5E20" }
  : score >= 6
    ? { label: "Good", color: "#388E3C" }
    : score >= 4
      ? { label: "Moderate", color: "#F57F17" }
      : { label: "Poor", color: "#D32F2F" };

const getConfidence = (score) => score >= 8
  ? { label: "High", detail: "High declared evidence confidence", color: "#1B5E20" }
  : score >= 4
    ? { label: "Moderate", detail: "Moderate declared evidence confidence", color: "#F57F17" }
    : { label: "Low", detail: "Low declared evidence confidence", color: "#D32F2F" };

const TransparencyBar = ({ value, color = "#0A8F79", inverse = false }) => {
  const width = Math.min(100, Math.max(0, Number(value || 0) * 10));
  return (
    <svg
      className={`transparency-bar${inverse ? " transparency-bar-inverse" : ""}`}
      viewBox="0 0 100 10"
      preserveAspectRatio="none"
      role="img"
      aria-label={`${score100(Number(value || 0), 2)} out of 100 transparency`}
    >
      <rect className="transparency-bar-track" x="0" y="0" width="100" height="10" rx="5" />
      <rect className="transparency-bar-fill" x="0" y="0" width={width} height="10" rx="5" fill={inverse ? "#FFFFFF" : color} />
    </svg>
  );
};

const scoreStatus = (score) => score >= 7
  ? { label: "Good", color: "#2E7D32", tone: "#E8F5E9" }
  : score >= 4
    ? { label: "Moderate", color: "#EF6C00", tone: "#FFF3E0" }
    : { label: "Critical", color: "#C62828", tone: "#FFEBEE" };

const methodologyBand = (score) => score >= 8.5
  ? { label: "Excellent", color: "#1B5E20", tone: "#E8F5E9" }
  : score >= 7
    ? { label: "Good", color: "#388E3C", tone: "#EDF7ED" }
    : score >= 5
      ? { label: "Average", color: "#9A6700", tone: "#FFF8E1" }
      : score >= 3
        ? { label: "Below average", color: "#EF6C00", tone: "#FFF3E0" }
        : { label: "Poor", color: "#C62828", tone: "#FFEBEE" };

const SUBSTAGES_ORDER = [
  "raw_material_extraction", "raw_material_processing",
  "manufacturing_pre_processing", "manufacturing_production",
  "use_phase_operation", "use_phase_maintenance",
  "eol_recycling", "eol_recovery", "eol_disposal",
  "logistics_transportation", "logistics_storage",
];

const SS_SHORT = {
  raw_material_extraction: "RM Ext.", raw_material_processing: "RM Proc.",
  manufacturing_pre_processing: "Mfg Pre", manufacturing_production: "Mfg Prod",
  use_phase_operation: "Use Op.", use_phase_maintenance: "Use Maint.",
  eol_recycling: "EOL Recycle", eol_recovery: "EOL Recovery", eol_disposal: "EOL Disposal",
  logistics_transportation: "Transport", logistics_storage: "Storage",
};

const SS_FULL = {
  raw_material_extraction: "Raw Material - Extraction",
  raw_material_processing: "Raw Material - Processing",
  manufacturing_pre_processing: "Manufacturing - Pre-Processing",
  manufacturing_production: "Manufacturing - Production",
  use_phase_operation: "Use Phase - Operation",
  use_phase_maintenance: "Use Phase - Maintenance",
  eol_recycling: "End-of-Life - Recycling",
  eol_recovery: "End-of-Life - Recovery",
  eol_disposal: "End-of-Life - Disposal",
  logistics_transportation: "Supply Chain - Transportation",
  logistics_storage: "Supply Chain - Storage",
};

const SS_STAGE = {
  raw_material_extraction: "Raw Material", raw_material_processing: "Raw Material",
  manufacturing_pre_processing: "Manufacturing", manufacturing_production: "Manufacturing",
  use_phase_operation: "Use Phase", use_phase_maintenance: "Use Phase",
  eol_recycling: "End-of-Life", eol_recovery: "End-of-Life", eol_disposal: "End-of-Life",
  logistics_transportation: "Supply Chain", logistics_storage: "Supply Chain",
};

const SS_STAGE_KEY = {
  raw_material_extraction: "raw_material", raw_material_processing: "raw_material",
  manufacturing_pre_processing: "manufacturing", manufacturing_production: "manufacturing",
  use_phase_operation: "use_phase", use_phase_maintenance: "use_phase",
  eol_recycling: "end_of_life", eol_recovery: "end_of_life", eol_disposal: "end_of_life",
  logistics_transportation: "logistics", logistics_storage: "logistics",
};

const getTransparencyBand = (score) => score >= 8
  ? { label: "Declared verified", color: "#1B5E20", tone: "#E8F5E9" }
  : score >= 4
    ? { label: "Self-reported", color: "#EF6C00", tone: "#FFF3E0" }
    : { label: "Estimated", color: "#C62828", tone: "#FFEBEE" };

const SectionHeader = ({ num, title, subtitle, icon: Icon }) => (
  <div className="advanced-section-heading">
    <div className="advanced-section-number">{String(num).padStart(2, "0")}</div>
    <div className="advanced-section-heading-copy">
      <div className="advanced-section-title-row">
        {Icon && <Icon className="w-4 h-4" />}
        <h2>{title}</h2>
      </div>
      {subtitle && <p>{subtitle}</p>}
    </div>
  </div>
);

const ScorePill = ({ score }) => {
  const status = scoreStatus(score);
  return <span className="report-status-pill" style={{ color: status.color, backgroundColor: status.tone }}>{status.label}</span>;
};

const formatValue = (value) => {
  if (value == null) return "Unavailable";
  const n = Number(value);
  if (Math.abs(n) >= 1000) return n.toLocaleString("en-US", { maximumFractionDigits: 1 });
  if (Math.abs(n) >= 100) return n.toFixed(1);
  return n.toFixed(2);
};

const HeadlineMetric = ({ icon: Icon, label, value, unit, color }) => (
  <div className="summary-impact-card">
    <div className="summary-impact-icon" style={{ color, backgroundColor: `${color}14` }}><Icon /></div>
    <div><span>{label}</span><strong>{value == null && unit === "See stage indicators" ? "Separate" : formatValue(value)}</strong><small>{unit}</small></div>
  </div>
);

const DimensionCard = ({ label, value, color }) => (
  <div className="summary-dimension-card" style={{ "--dimension-color": color }}>
    <span>{label}</span><strong>{score100(Number(value || 0), 2)}</strong><small>out of 100</small>
  </div>
);

const inferActionStage = (title = "") => {
  const match = STAGES_ORDER.find((stage) => title.toLowerCase().includes(STAGE_FULL[stage].toLowerCase()));
  if (match) return STAGE_FULL[match];
  if (title.toLowerCase().includes("supply chain")) return "Supply Chain";
  if (title.toLowerCase().includes("transparency")) return "All lifecycle stages";
  if (title.toLowerCase().includes("material") || title.toLowerCase().startsWith("switch ")) return "Raw Material";
  return "Cross-lifecycle";
};

const CriticalityLine = ({ rows }) => {
  if (!rows.length) return null;
  const width = 920;
  const height = 220;
  const padX = 38;
  const padTop = 22;
  const padBottom = 42;
  const max = Math.max(10, ...rows.map((row) => row.criticality));
  const x = (index) => padX + (index * (width - padX * 2)) / Math.max(1, rows.length - 1);
  const y = (value) => padTop + (1 - value / max) * (height - padTop - padBottom);
  const points = rows.map((row, index) => `${x(index)},${y(row.criticality)}`).join(" ");
  return (
    <div className="criticality-chart" data-testid="criticality-line-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Criticality by lifecycle substage">
        {[0, 2.5, 5, 7.5, 10].map((tick) => (
          <g key={tick}><line x1={padX} x2={width - padX} y1={y(tick)} y2={y(tick)} className="criticality-grid" /><text x={8} y={y(tick) + 4} className="criticality-axis">{tick * 10}</text></g>
        ))}
        <polyline points={points} className="criticality-line" />
        {rows.map((row, index) => (
          <g key={row.key}>
            <circle cx={x(index)} cy={y(row.criticality)} r="6" className="criticality-dot" />
            <text x={x(index)} y={y(row.criticality) - 12} textAnchor="middle" className="criticality-value-label">{score100(row.criticality, 2)}</text>
            <text x={x(index)} y={height - 13} textAnchor="middle" className="criticality-label">{SS_SHORT[row.key] || row.name.slice(0, 10)}</text>
          </g>
        ))}
      </svg>
    </div>
  );
};

export default function AssessmentResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [countryData, setCountryData] = useState({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/assessments/${id}`).then((response) => setData(response.data)).finally(() => setLoading(false));
    axios.get(`${API}/country-material-data`).then((response) => setCountryData(response.data || {})).catch(() => setCountryData({}));
  }, [id]);

  const paramsByStage = useMemo(() => data ? buildParamsByStage(data.results, "advanced") : {}, [data]);

  if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="w-8 h-8 animate-spin text-[#0A8F79]" /></div>;
  if (!data?.results) return <div className="p-8 text-[#617364]">Assessment not found</div>;

  const R = data.results;
  const upsi = Number(R.upsi || 0);
  const rating = getRatingBand(upsi);
  const buckets = productBuckets(R);
  const activeSubstages = SUBSTAGES_ORDER.filter((stage) => R.parameter_scores?.[stage]);
  const transparencyValues = activeSubstages.flatMap((stage) => PARAM_KEYS.map((parameter) => R.parameter_scores?.[stage]?.[parameter]?.transparency).filter((value) => Number.isFinite(Number(value))).map(Number));
  const avgTransparency = transparencyValues.length ? transparencyValues.reduce((sum, value) => sum + value, 0) / transparencyValues.length : 0;
  const confidence = getConfidence(avgTransparency);
  const substageTransparency = Object.fromEntries(activeSubstages.map((stage) => {
    const values = PARAM_KEYS.map((parameter) => R.parameter_scores?.[stage]?.[parameter]?.transparency)
      .filter((value) => Number.isFinite(Number(value)))
      .map(Number);
    return [stage, values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0];
  }));
  const bom = R.mass_fractions || data.bom_items || [];
  const productMass = Number(R.unit_summary?.audit?.effective_product_mass_kg) || bom.reduce((sum, item) => sum + (Number(item.quantity) || 1) * (Number(item.weight_kg) || 0), 0);
  const fallbackMetrics = aggregateStageInputs(data.stage_inputs || {}, buildCountryInferredRows(bom, countryData));
  const aggregate = (parameter) => R.unit_summary?.parameter_aggregates?.[parameter]
    ? { value: R.unit_summary.parameter_aggregates[parameter].display_value, unit: R.unit_summary.parameter_aggregates[parameter].display_unit }
    : { value: fallbackMetrics[parameter]?.value || 0, unit: PARAM_UNITS[parameter] };
  const actions = top5Actions({ results: R, paramsByStage, mode: "advanced", bom });
  const topActions = actions.slice(0, 3);
  const criticalityRows = criticalityLadder(R, "advanced");
  const unitAudit = R.unit_summary?.audit;
  const unitIssues = unitAudit?.issues || [];
  const stageRows = STAGES_ORDER.filter(stage => R.stage_si?.[stage] != null).map((stage) => ({ key: stage, label: STAGE_FULL[stage], score: Number(R.stage_si?.[stage] || 0), status: classifyStage(Number(R.stage_si?.[stage] || 0)) }));
  const actionConfidence = (action) => {
    const stage = STAGES_ORDER.find((key) => action.title.toLowerCase().includes(STAGE_FULL[key].toLowerCase()));
    if (!stage) return confidence.label;
    const values = Object.values(paramsByStage[stage] || {}).map((item) => item.transparency || 0);
    const score = values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : avgTransparency;
    return getConfidence(score).label;
  };

  const handlePDF = async () => {
    setExporting(true);
    try {
      await exportAdvancedReportToPdf(
        data,
        countryData,
        `TOSS_Advanced_${data.product_name.replace(/\s/g, "_")}.pdf`,
      );
    } finally {
      setExporting(false);
    }
  };

  const lifecycleDiagnostics = STAGES_ORDER.map((stageKey, stageIndex) => ({
    key: stageKey,
    index: stageIndex + 1,
    label: stageKey === "logistics" ? "Supply Chain" : STAGE_FULL[stageKey],
    substages: activeSubstages.filter((substage) => SS_STAGE_KEY[substage] === stageKey).map((substage) => {
      const score = Number(R.ssi?.[substage] || 0);
      const parameterEntries = PARAM_KEYS.map((parameter) => ({
        key: parameter,
        score: Number(R.parameter_scores?.[substage]?.[parameter]?.adjusted),
      })).filter((entry) => Number.isFinite(entry.score));
      const weakest = parameterEntries.sort((a, b) => a.score - b.score)[0];
      return {
        key: substage,
        label: SS_FULL[substage]?.split(" - ").slice(1).join(" - ") || SS_FULL[substage],
        score,
        band: methodologyBand(score),
        evidence: getTransparencyBand(substageTransparency[substage] || 0),
        weakest: weakest ? `${PL[weakest.key]} (${score100(weakest.score, 2)})` : "Not available",
      };
    }),
  })).filter((stage) => stage.substages.length);
  const matrixGroups = [activeSubstages.slice(0, 6), activeSubstages.slice(6)].filter((group) => group.length);

  return (
    <div className="advanced-result-page" data-testid="assessment-result">
      <div className="advanced-report-toolbar">
        <Button variant="ghost" onClick={() => navigate("/advanced/assessments")} className="text-[#617364]" data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <div className="advanced-toolbar-note">TOSS Advanced Result{data.revision_of && <button className="block underline text-xs" onClick={()=>navigate(`/advanced/assessments/${data.revision_of}`)}>View original assessment</button>}</div>
        <Button disabled={exporting} onClick={handlePDF} className="advanced-download-button" data-testid="download-pdf-btn">
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}{exporting ? "Preparing report..." : "Download PDF report"}
        </Button>
      </div>

      <main className="advanced-report">
        <section className="toss-summary-label" data-testid="section-summary-label" data-pdf-page>
          <div className="summary-label-header">
            <div className="advanced-cover-brand"><div className="advanced-logo-shell"><img src={LOGO_URL} alt={BRAND.short} /></div><span>Product Sustainability Index</span></div>
            <div className="summary-report-id"><small>Assessment ID</small><strong>{data.id || id}</strong></div>
          </div>
          <div className="summary-hero">
            <div className="summary-product">
              <div className="advanced-cover-kicker"><span>Assessment Report</span><span className="advanced-cover-mode">Advanced</span></div>
              <h1>{data.product_name}</h1><p>{data.product_type || "Product assessment"}</p>
              <div className="summary-product-meta">
                <div><Box aria-hidden="true" /><span><small>Reporting functional unit</small><strong>{data.functional_unit || "Not specified"}</strong></span></div>
                <div><Weight aria-hidden="true" /><span><small>Product mass</small><strong>{formatValue(productMass)} kg</strong></span></div>
                <div><CalendarDays aria-hidden="true" /><span><small>Report date</small><strong>{new Date(data.created_at).toLocaleDateString("en-GB")}</strong></span></div>
              </div>
            </div>
            <aside className="summary-score-panel"><small>Overall TOSS Index</small><strong>{score100(upsi, 2)}</strong><span>out of 100</span><i className="summary-score-track"><span style={{ width: `${Math.max(0, Math.min(100, upsi * 10))}%` }} /></i><b style={{ color: rating.color, backgroundColor: `${rating.color}16` }}>{rating.label}</b><p>Product Sustainability Index · {R.aggregation?.method === "process_b" ? "Process B" : "Earlier method"}</p><Leaf className="summary-score-leaf" aria-hidden="true" /><footer>A more sustainable<br />tomorrow</footer></aside>
          </div>
          <div className="summary-label-body">
            <div className="summary-primary-grid">
              <div className="summary-card-block">
                <div className="summary-block-title"><span>ESGE performance</span><small>Primary dimension scores</small></div>
                <div className="summary-dimensions">
                  <DimensionCard label="Environmental" value={buckets.environmental} color={ESGE_COLORS.environmental} />
                  <DimensionCard label="Social" value={buckets.social} color={ESGE_COLORS.social} />
                  <DimensionCard label="Governance" value={buckets.governance} color={ESGE_COLORS.governance} />
                  <DimensionCard label="Economic" value={buckets.economic} color={ESGE_COLORS.economic} />
                </div>
              </div>
              <div className="summary-confidence" style={{ "--confidence-color": confidence.color }}><span>Data confidence</span><strong>{confidence.label}</strong><b>{score100(avgTransparency, 2)} / 100</b><small>{confidence.detail}</small></div>
            </div>
            <div className="summary-impact-grid">
              <HeadlineMetric icon={Cloud} label="GWP" value={aggregate("gwp").value} unit={aggregate("gwp").unit} color="#2E7D32" />
              <HeadlineMetric icon={Droplets} label="Water" value={aggregate("water").value} unit={aggregate("water").unit} color="#1976D2" />
              <HeadlineMetric icon={Recycle} label="Circularity" value={aggregate("circularity").value} unit={aggregate("circularity").unit} color="#00897B" />
            </div>
            <div className="summary-stage-block">
              <div className="summary-block-title"><span>Lifecycle-stage status</span><small>One status per lifecycle stage</small></div>
              <div className="summary-stages">
                {stageRows.map((stage, index) => (
                  <div className="summary-stage" key={stage.key} style={{ "--stage-color": stage.status.color }}>
                    <div className="summary-stage-node">{String(index + 1).padStart(2, "0")}</div>
                    <div className="summary-stage-copy">
                      <small>{stage.label}</small>
                      <div className="summary-stage-reading"><strong>{score100(stage.score, 2)}</strong><span>/ 100</span></div>
                      <ScorePill score={stage.score} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="summary-priorities">
              <div className="summary-block-title"><span>Three highest-priority improvements</span><small>Ranked by current stage performance</small></div>
              <div className="summary-priority-list">
                {topActions.map((action, index) => <div className="summary-priority" key={`${action.title}-${index}`}><b>{index + 1}</b><p>{action.title}</p><strong>{action.impact}</strong></div>)}
                {!topActions.length && <p className="summary-empty">No high-priority actions were generated for the current result.</p>}
              </div>
            </div>
          </div>
        </section>

        <section data-testid="section-performance-breakdown" data-pdf-page data-pdf-break-before>
          <SectionHeader num={2} title="Lifecycle Diagnostic Breakdown" subtitle="Official substage results, organized by the five lifecycle stages." icon={BarChart3} />
          <div className="diagnostic-legend"><span><i className="diagnostic-legend-score" />Parameter Avg.</span><span><i className="diagnostic-legend-band" />Methodology band</span><span><i className="diagnostic-legend-evidence" />Evidence level</span></div>
          <div className="diagnostic-groups">
            {lifecycleDiagnostics.map((stage) => (
              <article className="diagnostic-group" key={stage.key}>
                <header className="diagnostic-group-head"><span>{String(stage.index).padStart(2, "0")}</span><h3>{stage.label}</h3><small>{stage.substages.length} {stage.substages.length === 1 ? "substage" : "substages"}</small></header>
                <div className="diagnostic-list">
                  {stage.substages.map((substage) => (
                    <div className="diagnostic-row" key={substage.key}>
                      <div className="diagnostic-identity"><strong>{substage.label}</strong><small>Weakest: {substage.weakest}</small></div>
                      <div className="diagnostic-ssi"><small>Parameter Avg.</small><strong>{score100(substage.score, 2)}</strong><span>/ 100</span></div>
                      <span className="diagnostic-band" style={{ color: substage.band.color, backgroundColor: substage.band.tone }}>{substage.band.label}</span>
                      <div className="diagnostic-evidence"><small>Evidence</small><strong style={{ color: substage.evidence.color }}>{substage.evidence.label}</strong></div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
          <div className="performance-note"><Leaf /><p>No new group averages are introduced here. Parameter Avg., transparency and weakest-parameter values come directly from the existing normalized and transparency-adjusted calculation outputs.</p></div>
        </section>

        <section data-testid="section-improvement-plan" data-pdf-page data-pdf-break-before>
          <SectionHeader num={3} title="Improvement Plan" subtitle="Priorities based on current performance. Score changes require a complete recalculation." icon={Target} />
          <div className="improvement-table-wrap"><table className="improvement-table"><thead><tr><th>Priority action</th><th>Score change</th><th>Lifecycle stage</th><th>Priority level</th><th>Confidence</th></tr></thead><tbody>
            {actions.map((action) => { const impact = action.impact; return <tr key={action.title}><td><div className="action-title-cell"><b>{action.priority}</b><span><strong>{action.title}</strong><small>{action.rationale}</small></span></div></td><td><span className="gain-value">Requires recalculation</span></td><td>{inferActionStage(action.title)}</td><td><span className={`impact-level impact-${impact.toLowerCase()}`}>{impact}</span></td><td>{actionConfidence(action)}</td></tr>; })}
          </tbody></table></div>
          <div className="criticality-section"><div className="summary-block-title"><span>Criticality profile</span><small>Higher values indicate greater intervention priority</small></div><CriticalityLine rows={criticalityRows} /></div>
        </section>

        <section data-testid="section-verification" data-pdf-page data-pdf-break-before>
          <SectionHeader num={4} title="Verification and Methodology" subtitle="A concise record of scope, units, evidence quality and validation." icon={ShieldCheck} />
          <p className="text-sm mb-4">{evidenceSummary(R)}</p><p className="text-sm mb-4">{stageScoreNote(R)}</p><div className="verification-grid">{!R.unit_basis_version && <p className="text-amber-800">Legacy assessment: saved results predate explicit unit-basis validation.</p>}
            <div className="verification-card"><small>Reporting functional unit</small><strong>{data.functional_unit || "Not specified"}</strong><p>Applied to product-level impact output.</p></div>
            <div className="verification-card"><small>Product mass and BOM</small><strong>{formatValue(productMass)} kg</strong><p>{bom.length} {bom.length === 1 ? "BOM line" : "BOM lines"}; {R.process_validation_version ? "quantity x unit mass reconciled." : "current mass validation not recorded."}</p></div>
            <div className="verification-card"><small>System boundary</small><strong>Assessed lifecycle coverage</strong><p>{Object.keys(R.stage_si || {}).length} lifecycle stages and {activeSubstages.length} assessed substages.</p></div>
            <div className="verification-card"><small>Benchmark source</small><strong>Country / world / product</strong><p>Selected country datasets and saved product benchmarks.</p></div>
            <div className="verification-card"><small>Transparency classification</small><strong style={{ color: confidence.color }}>{confidence.detail}</strong><p>Average evidence score {score100(avgTransparency, 2)} / 100.</p></div>
            <div className="verification-card"><small>Calculation version</small><strong>{R.methodology_version || "Legacy / version not recorded"}</strong><p>Scoring methodology unchanged.</p></div>
          </div>
          <div className="verification-wide-grid">
            <div className="verification-wide-card"><div className="summary-block-title"><span>Units used</span><small>Canonical scoring contract</small></div><div className="unit-chip-grid">{PARAM_KEYS.map((parameter) => <span key={parameter}><b>{PL[parameter]}</b><small>{advancedUnit(unitAudit?.canonical_units?.[parameter] || PARAM_UNITS[parameter])}</small></span>)}</div></div>
            <div className="verification-wide-card verification-findings"><div className="summary-block-title"><span>Unit-validation findings</span><small>Automated input and benchmark checks</small></div><strong className={unitIssues.some((issue) => issue.severity === "error") ? "finding-error" : "finding-ok"}>{unitIssues.length} {unitIssues.length === 1 ? "finding" : "findings"}</strong><p>{unitIssues.length ? `${unitIssues.filter((issue) => issue.severity === "error").length} blocking and ${unitIssues.filter((issue) => issue.severity !== "error").length} advisory findings. See the Technical Appendix for each conversion and responsible function.` : (!R.process_validation_version ? "Legacy result: current validation was not recorded." : "Saved automated checks recorded no unit errors; this is not independent verification.")}</p></div>
          </div>
          <div className="verification-footer-strip"><div><small>Assumptions and inferred values</small><strong>{(unitAudit?.conversions?.length || 0) + (unitAudit?.basis_conversions?.length || 0)} documented {((unitAudit?.conversions?.length || 0) + (unitAudit?.basis_conversions?.length || 0)) === 1 ? "conversion" : "conversions"}</strong></div><div><small>Assessment ID</small><strong>{data.id || id}</strong></div><div className="qr-placeholder"><span>QR</span><p>Assessment reference</p></div></div>
        </section>

        <section className="technical-appendix" data-testid="section-technical-appendix" data-pdf-page data-pdf-break-before>
          <SectionHeader num={5} title="Technical Appendix" subtitle="Complete calculation evidence for review, audit and reproducibility." icon={Sparkles} />
          <div className="appendix-intro"><Factory /><p>This appendix intentionally contains the dense evidence removed from the decision pages. It preserves raw inputs, normalized scores, transparency adjustments, weighting, ESGE calculations, unit conversions, BOM data and detailed formulas.</p></div>
<div className="appendix-block"><h3>A. Stage and ESGE calculations</h3><SustainabilityOverview results={R} /></div>
          <div className="appendix-block" data-pdf-break-before><h3>B. Normalized parameter evidence</h3><div className="appendix-card"><h4>12-parameter score distribution</h4><ParameterDonuts mode="advanced" paramsByStage={paramsByStage} /></div><div className="appendix-two-col"><div className="appendix-card"><h4>Dominant parameters</h4><ParameterDominance scoreScale={10} paramsByStage={paramsByStage} /></div><div className="appendix-card"><h4>Pollution drivers</h4><PollutionDriver scoreScale={10} paramsByStage={paramsByStage} /></div></div><div className="appendix-card"><h4>Parameter variability</h4><VariabilityIndicator scoreScale={10} paramsByStage={paramsByStage} /></div></div>
          <div className="appendix-block" data-pdf-break-before>
            <h3>C. Parameter x substage matrix</h3>
            <p className="appendix-explainer">The matrix is split into two readable panels. Values remain the transparency-adjusted parameter scores used by the calculation engine.</p>
            <div className="pdf-heat-legend" aria-hidden="true"><span>Low</span><i /><span>High</span></div>
            <div className="matrix-panels" data-testid="param-matrix-table">
              {matrixGroups.map((group, groupIndex) => (
                <div className="appendix-card appendix-table-scroll matrix-panel" key={`matrix-${groupIndex}`}>
                  <h4>{groupIndex === 0 ? "Lifecycle substages 01-06" : "Lifecycle substages 07-11"}</h4>
                  <table className="parameter-matrix">
                    <thead><tr><th>Parameter</th>{group.map((stage) => <th key={stage}>{SS_SHORT[stage]}</th>)}</tr></thead>
                    <tbody>
                      {PARAM_KEYS.map((parameter) => <tr key={parameter}><td>{PL[parameter]}</td>{group.map((stage) => { const value = R.parameter_scores?.[stage]?.[parameter]?.adjusted; return <td key={stage} title={R.exclusions?.[stage]?.[parameter] || ""} style={{ backgroundColor: value == null ? "#f1f5f3" : heatColor(value) }}>{value == null ? "N/A" : score100(value, 1)}</td>; })}</tr>)}
                      <tr className="matrix-total"><td>Parameter Avg.</td>{group.map((stage) => <td key={stage}>{score100(Number(R.ssi?.[stage] || 0), 2)}</td>)}</tr>
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
          <div className="appendix-block" data-pdf-break-before><h3>D. Root causes and traceability</h3><div className="appendix-card"><RootCauseAnalysis results={R} paramsByStage={paramsByStage} mode="advanced" /></div><div className="appendix-card" data-pdf-break-before><InputAudit data={data} paramsByStage={paramsByStage} mode="advanced" countryData={countryData} /></div></div>
          <div className="appendix-block transparency-appendix-block" data-pdf-break-before>
            <h3>E. Data transparency by substage</h3>
            <p className="appendix-explainer">Average evidence transparency across applicable assessment parameters. The score and evidence band show how strongly each substage is supported by traceable source data.</p>
            <div className="appendix-card appendix-table-scroll">
              <table className="transparency-table" data-testid="substage-transparency-table">
                <thead><tr><th>Lifecycle stage</th><th>Substage</th><th>Avg. transparency</th><th>Evidence band</th><th>Visual</th></tr></thead>
                <tbody>
                  {activeSubstages.map((stage) => {
                    const value = substageTransparency[stage];
                    const band = getTransparencyBand(value);
                    return (
                      <tr key={stage}>
                        <td>{SS_STAGE[stage]}</td>
                        <td>{SS_FULL[stage]}</td>
                        <td><span className="transparency-score" style={{ color: band.color }}><strong>{score100(value, 2)}</strong><small>/ 100</small></span></td>
                        <td><span className="transparency-band" style={{ color: band.color, backgroundColor: band.tone }}>{band.label}</span></td>
                        <td><TransparencyBar value={value} color={band.color} /></td>
                      </tr>
                    );
                  })}
                  <tr className="transparency-total">
                    <td colSpan="2">Overall average (all substages x parameters)</td>
                    <td>{score100(avgTransparency, 2)} / 100</td>
                    <td>{getTransparencyBand(avgTransparency).label}</td>
                    <td><TransparencyBar value={avgTransparency} inverse /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
        <AssessmentMethodAudit results={R} />
        <footer className="advanced-report-footer"><div><img src={LOGO_URL} alt="" /><span>TOSS Index - {BRAND.full}</span></div><p>Advanced assessment - {R.methodology_version || "TOSS Advanced V1"}</p></footer>
      </main>
    </div>
  );
}
